import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, Volume2, CheckCircle2, Clock, RefreshCw } from 'lucide-react';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { API_URL } from '@/config/api';

// API_URL imported from centralized config

const TTSProgressModal = ({ 
  isOpen, 
  onClose, 
  segmentsTotal, 
  segmentsCompleted, 
  isComplete, 
  isRegeneration = false,
  coverImage = null,
  manuscriptId = null
}) => {
  const [isRestarting, setIsRestarting] = useState(false);
  const [restartMessage, setRestartMessage] = useState(null);
  
  const progress = segmentsTotal > 0 ? (segmentsCompleted / segmentsTotal) * 100 : 0;
  
  // Build cover URL from manuscript ID if no direct cover image provided
  // Add cache-busting parameter to ensure fresh cover images
  const coverUrl = coverImage || (manuscriptId ? `${API_URL}/api/covers/${manuscriptId}?v=${Date.now()}` : null);

  // Handle restart - only called when user explicitly clicks button
  const handleRestart = async () => {
    if (!manuscriptId) {
      setRestartMessage({ type: 'error', text: 'Cannot restart: Missing manuscript ID' });
      return;
    }
    
    setIsRestarting(true);
    setRestartMessage(null);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/manuscripts/${manuscriptId}/restart-tts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setRestartMessage({
          type: 'success',
          text: data.message || 'TTS restart initiated. Processing will resume from the last checkpoint.'
        });
      } else {
        setRestartMessage({
          type: 'error',
          text: data.detail || 'Failed to restart TTS processing.'
        });
      }
    } catch (error) {
      console.error('Failed to restart TTS:', error);
      // Network errors often indicate CORS or connectivity issues
      const errorText = error.message?.includes('fetch') || error.message?.includes('network') 
        ? 'Network error - please check your connection and try again.'
        : 'Failed to restart TTS processing. Please try again.';
      setRestartMessage({
        type: 'error',
        text: errorText
      });
    } finally {
      setIsRestarting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      // Allow closing at any time - TTS continues in background
      if (!open && onClose) {
        onClose();
      }
    }}>
      <DialogContent className="w-[90vw] sm:max-w-md mx-auto">
        {/* NewAudia Logo */}
        <div className="flex justify-center pt-2 pb-1">
          <img 
            src="/newaudia-logo.jpg" 
            alt="NewAudia" 
            className="h-10 w-auto"
          />
        </div>
        
        <DialogHeader>
          <DialogTitle className="flex items-center justify-center gap-2 text-xl">
            {isComplete ? (
              <>
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                Audio Generation Complete
              </>
            ) : (
              <>
                <Loader2 className="h-6 w-6 text-purple-600 animate-spin" />
                Generating Audio
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Book Cover Image - shown when not complete */}
          {!isComplete && coverUrl && (
            <div className="flex justify-center">
              <img 
                src={coverUrl}
                alt="Book Cover"
                className="h-40 w-auto rounded-lg shadow-lg object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Restart Success/Error Message */}
          {restartMessage && (
            <div className={`rounded-lg p-4 ${
              restartMessage.type === 'success' 
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}>
              <p className={`text-sm ${
                restartMessage.type === 'success'
                  ? 'text-green-800 dark:text-green-200'
                  : 'text-red-800 dark:text-red-200'
              }`}>
                {restartMessage.text}
              </p>
            </div>
          )}

          {!isComplete && (
            <>
              {/* Time Estimate Banner */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                      This process normally requires 30 to 60 minutes to complete.
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      Please be patient. You can close this dialog and check back later.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Volume2 className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                      {isRegeneration 
                        ? 'Updating Audio for Modified Segments'
                        : 'Generating Audio for All Chapters'
                      }
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                      {isRegeneration
                        ? 'Only segments that were refined or merged are being regenerated.'
                        : 'Creating high-quality audio files for your entire manuscript.'
                      }
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Progress:</span>
              <span className="font-medium">
                {segmentsCompleted} / {segmentsTotal} segments
              </span>
            </div>
            
            <Progress value={progress} className="h-3" />
            
            <p className="text-xs text-center text-gray-500 dark:text-gray-400">
              {isComplete 
                ? '✓ All audio files generated successfully'
                : 'Audio generation continues in background...'
              }
            </p>
          </div>

          {isComplete && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="text-sm text-green-800 dark:text-green-200 text-center">
                Your manuscript audio has been updated and is ready to use!
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-center gap-3 pt-2">
            {/* Restart Button - always available when not complete and has manuscriptId */}
            {!isComplete && manuscriptId && (
              <Button 
                variant="outline"
                onClick={handleRestart}
                disabled={isRestarting}
                data-testid="restart-tts-btn"
              >
                {isRestarting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Restarting...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Restart TTS
                  </>
                )}
              </Button>
            )}
            
            {/* Close/Done Button */}
            <Button 
              variant={isComplete ? "default" : "outline"}
              onClick={onClose}
              className={isComplete ? "bg-purple-600 hover:bg-purple-700" : ""}
            >
              {isComplete ? "Done" : "Close"}
            </Button>
          </div>
          
          {!isComplete && (
            <p className="text-xs text-center text-gray-400">
              Closing this dialog will not cancel the audio generation process.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TTSProgressModal;
